/**
 * ImpersonationContext — gestisce lo stato della sessione di impersonazione.
 *
 * Il token viene letto dai query params all'avvio (/impersonate?token=...)
 * e salvato in sessionStorage (per-tab, non persiste tra tab diverse).
 * api.ts lo usa come Bearer token al posto del token Supabase.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

const SESSION_KEY = 'km_impersonation'

export interface ImpersonationState {
  token: string
  tenant_id: string
  tenant_name: string
  impersonator_email: string
  is_readonly: boolean
  expires_at: string // ISO
}

interface ImpersonationContextValue {
  impersonation: ImpersonationState | null
  endImpersonation: () => void
}

const ImpersonationContext = createContext<ImpersonationContextValue>({
  impersonation: null,
  endImpersonation: () => {},
})

export function useImpersonation() {
  return useContext(ImpersonationContext)
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(() => {
    // 1. Controlla sessionStorage (tab già avviata)
    try {
      const raw = sessionStorage.getItem(SESSION_KEY)
      if (raw) {
        const state = JSON.parse(raw) as ImpersonationState
        if (new Date(state.expires_at) > new Date()) return state
        sessionStorage.removeItem(SESSION_KEY)
      }
    } catch { /* ignore */ }

    // 2. Controlla URL params (nuova tab aperta dal pannello admin) — sincrono!
    // Deve avvenire qui e non in useEffect, altrimenti ProtectedRoute reindirizza
    // a /login prima che React esegua gli effetti.
    const params = new URLSearchParams(window.location.search)
    const token = params.get('impersonation_token')
    if (token) {
      const state: ImpersonationState = {
        token,
        tenant_id: params.get('tenant_id') ?? '',
        tenant_name: params.get('tenant_name') ?? '',
        impersonator_email: params.get('impersonator') ?? '',
        is_readonly: params.get('readonly') !== 'false',
        expires_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
      }
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
      return state
    }

    return null
  })

  // Rimuovi i params dall'URL senza ricaricare (il token è già salvato in sessionStorage)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('impersonation_token')) {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  function endImpersonation() {
    sessionStorage.removeItem(SESSION_KEY)
    setImpersonation(null)
    // Ricarica per tornare alla sessione Supabase normale
    window.location.href = '/'
  }

  return (
    <ImpersonationContext.Provider value={{ impersonation, endImpersonation }}>
      {children}
    </ImpersonationContext.Provider>
  )
}
